// Constraint entries of the zinc-puzzle document, as in docs/CONSTRAINTS.md. The core reads the
// same JSON (core/src/plugins); the web app draws from it.
import type { Rc } from './puzzle';

/** Drawing overrides on any entry; never logic. */
export interface ConstraintStyle {
  color?: string;
  /** An arrow's circle, when the source draws it in a colour of its own (SudokuPad's "bulb"). */
  circleColor?: string;
  width?: number;
  [key: string]: unknown;
}

interface Base {
  style?: ConstraintStyle;
  label?: string;
  /** Round 7c: whether the rule reads the cells' values, which a value rule (negative digits,
   * contract killers, liars) makes differ from their digits. Killer, arrow, edge-sum, ratio,
   * difference, whispers and zipper read values in such a puzzle unless this is false. */
  values?: boolean;
}

interface CellsEntry extends Base {
  cells: Rc[];
}

export type ExtraRegion = CellsEntry & { type: 'extra-region' };
export type Diagonal = Base & { type: 'diagonal'; direction: 'positive' | 'negative' };
export type DisjointGroups = Base & { type: 'disjoint-groups' };

export type Odd = CellsEntry & { type: 'odd' };
export type Even = CellsEntry & { type: 'even' };
export type Minimum = CellsEntry & { type: 'minimum' };
export type Maximum = CellsEntry & { type: 'maximum' };
export type Fortress = CellsEntry & { type: 'fortress' };

export const LINE_TYPES = [
  'thermo',
  'slow-thermo',
  'double-arrow',
  'renban',
  'nabner',
  'whispers',
  'palindrome',
  'region-sum',
  'between',
  'lockout',
  'entropic',
  'modular',
  'parity',
  'zipper',
  'kropki-line',
  'sequence',
  'bulbless-thermo',
  'pointing-thermo',
] as const;
export type LineType = (typeof LINE_TYPES)[number];
export type Line = CellsEntry & {
  type: LineType;
  /** whispers (default 5), lockout (default 4). */
  difference?: number;
  /** modular (default 3). */
  modulus?: number;
  /** renban: lines with the same group are one set ("each cross is a renban"). */
  group?: string;
};
export type Arrow = CellsEntry & { type: 'arrow'; circle: Rc[] };

export type Killer = CellsEntry & {
  type: 'killer';
  sum?: number | null;
  repeats?: boolean;
  /** Digits that share an edge within the cage aren't consecutive. */
  nonConsecutive?: boolean;
  /** Each digit in the cage appears as many times as its value (repeats allowed). */
  counting?: boolean;
  /** Possible totals when the rules give a choice ("sum to either 1 or 12"). */
  sums?: number[];
};
export type LittleKiller = Base & {
  type: 'little-killer';
  clue: Rc;
  direction: [number, number];
  sum: number;
};
export const OUTSIDE_TYPES = [
  'sandwich',
  'x-sum',
  'skyscraper',
  'frame-sum',
  'battlefield',
  'numbered-room',
  'outside-digits',
  'increasing-run',
  'up-to-sum',
] as const;
export type OutsideType = (typeof OUTSIDE_TYPES)[number];
export type OutsideClue = Base & {
  type: OutsideType;
  clue: Rc;
  /** sandwich, x-sum, frame-sum, battlefield. */
  sum?: number;
  /** skyscraper. */
  count?: number;
  /** numbered-room: the digit that sits in the Nth cell; increasing-run: the longest run. */
  value?: number;
  /** outside-digits: the digits that appear among the first `count` cells (default 3). */
  digits?: (string | number)[];
  /** frame-sum: cells counted (default: the box size in that direction). */
  length?: number;
};
/** `text`: the setter's own lines, wildcards included ("2?", "3?"), drawn as written; the rule
 * is `digits`. */
export type Quadruple = CellsEntry & { type: 'quadruple'; digits: string[]; text?: string[] };

export const EDGE_TYPES = [
  'difference',
  'ratio',
  'edge-sum',
  'greater-than',
  'kropki-either',
] as const;
export type EdgeType = (typeof EDGE_TYPES)[number];
export type EdgeClue = CellsEntry & { type: EdgeType; value?: number; negative?: false };
/** "All dots given": every adjacent pair without a clue of the family avoids `values`. */
export type NegativeEdge = Base & {
  type: 'difference' | 'ratio' | 'edge-sum';
  negative: true;
  values: number[];
};

export type AntiKnight = Base & { type: 'anti-knight' };
export type AntiKing = Base & { type: 'anti-king' };
export type NonConsecutive = Base & { type: 'non-consecutive' };
export type Clone = CellsEntry & { type: 'clone'; copy: Rc[] };
export type MagicSquare = CellsEntry & {
  type: 'magic-square';
  /** false: only the rows and columns share the sum ("within every box, ..."). Default true. */
  diagonals?: boolean;
};
/** Chess pieces on digits and coloured cages: rules in words, not drawn. */
export type Queen = Base & { type: 'queen'; digits: (string | number)[] };
export type KnightDistinct = Base & { type: 'knight-distinct'; digits: (string | number)[] };
export type ChessKings = Base & {
  type: 'chess-kings';
  /** How many digits are kings (found while solving); default 2. */
  count?: number;
  /** The king digits, when the rules name them. */
  digits?: (string | number)[];
};
export type CageColours = Base & { type: 'cage-colours'; colours?: number };
/** Pack 8 (BremSter's fog playlist, 2026-11-01). */
export type Friendly = CellsEntry & { type: 'friendly' };
export type BoxIndexer = CellsEntry & { type: 'box-indexer' };
export type Diversity = CellsEntry & { type: 'diversity' };
/** The four cells around a corner, clockwise from the top left, and the wheel's digits in the
 * same order. */
export type Wheel = CellsEntry & { type: 'wheel'; digits: string[] };
export type AntiDiagonal = Base & {
  type: 'anti-diagonal';
  direction: 'positive' | 'negative';
  /** How many different digits the diagonal may hold (default 3). */
  count?: number;
};
/** One of several rules holds; the solver finds which. */
/** One of the options holds. `drawn: false`: the setter's drawing shows it, the app draws none
 * of the options (Such Möhrchen's carrots, each a minimum or a maximum). */
export type Either = Base & { type: 'either'; options: ConstraintEntry[]; drawn?: boolean };

/** Pack 6: shading. The solver finds a shading alongside the digits (each cell shaded or
 * not); rules about one colour speak of the unshaded cells with `unshaded: true`. Only the
 * circles of `view-count` and `area-clues` are drawn; the rest is in words. */
interface ShadingBase extends Base {
  unshaded?: boolean;
}
export type Shaded = ShadingBase & { type: 'shaded'; cells: Rc[] };
export type Connected = ShadingBase & { type: 'connected' };
export type NoSquare = ShadingBase & { type: 'no-2x2' };
export type AreaSize = ShadingBase & { type: 'area-size'; size: number };
export type ReachEdge = ShadingBase & { type: 'reach-edge' };
export type ShadeCount = ShadingBase & { type: 'shade-count'; cells: Rc[]; count: number };
/** The cell is unshaded; its digit (or `value`) counts the unshaded cells it sees. */
export type ViewCount = Base & { type: 'view-count'; cell: Rc; value?: number };
export type ShadedFortress = Base & { type: 'shaded-fortress' };
export type ShadedRatio = Base & { type: 'shaded-ratio'; value?: number };
export type AreaClues = ShadingBase & { type: 'area-clues'; cells: Rc[] };
export type AreaDigits = ShadingBase & { type: 'area-digits' };
/** `ends` may sit just outside the grid: the snake comes in from there. */
export type Snake = Base & { type: 'snake'; length?: number; ends?: [Rc, Rc] };
export type SnakeTurns = Base & { type: 'snake-turns'; turn?: 'even' | 'odd' };
export type ShadedWhispers = Base & { type: 'shaded-whispers'; difference?: number };
/** A cage whose `sum` counts only its unshaded cells; drawn like a killer cage. */
export type UnshadedSum = Base & { type: 'unshaded-sum'; cells: Rc[]; sum: number };
/** The left and right edges join with a half twist (for the shading rules). */
export type Mobius = Base & { type: 'mobius' };

/** Pack 9 (the needs-a-plugin list, 2026-11-02). */
export type ProductLine = CellsEntry & { type: 'product-line'; product: number };
/** The listed digits appear among the cells: a rule in words, not drawn. */
export type Contains = CellsEntry & { type: 'contains'; digits: (string | number)[] };
export type SameParity = CellsEntry & { type: 'same-parity' };
export type CountingCircles = CellsEntry & { type: 'counting-circles' };
export type NeverBoth = Base & { type: 'never-both'; parity?: 'even' | 'odd' };
export type NoConsecutiveTriple = Base & { type: 'no-consecutive-triple'; diagonals?: boolean };
export type DominantLine = CellsEntry & { type: 'dominant-line' };
/** A marked 2×2 (its four cells), or `negative`: every checkerboard is marked. */
export type Battenburg = Base & { type: 'battenburg'; cells?: Rc[]; negative?: boolean };
export type SnakeSequence = Base & { type: 'snake-sequence'; digits: string | (string | number)[] };

/** One-off rules (item 5, 2026-11-04). Rules in words, not drawn. */
/** Somewhere along the line (`cells`), or along `lines` that meet, `length` cells in a row
 * (default 2) whose neighbours differ by at least `difference` (default 5): a German whisper
 * hidden beneath it. */
export type WhisperSomewhere = Base & {
  type: 'whisper-somewhere';
  cells?: Rc[];
  lines?: Rc[][];
  length?: number;
  difference?: number;
};
/** An arrow in a cell (`cells`: that cell) pointing along `direction` ([row step, column step]):
 * its digit is the sum of the nearest digits it points at (`pointer-sum`), or of those their
 * own number of steps away (`hitpoint-arrow`). The setter's arrow stays drawn. */
export type PointerSum = CellsEntry & { type: 'pointer-sum'; direction: [number, number] };
/** A small arrow in `cells`: the digit it points at is on its line again in every box the line
 * goes on through. The setter's arrow stays drawn. */
export type BoxPointer = CellsEntry & { type: 'box-pointer'; direction: [number, number] };
/** The first of `cells` holds their largest digit (`clue`: the type of the clue it is about). A
 * rule in words, not drawn. */
export type LargestFirst = CellsEntry & { type: 'largest-first'; clue?: string };
/** A greek carpet: the spiral (`cells`, from the end it rolls out from) holds the digits of the
 * straight line on from that end, in order. The setter's line stays drawn. */
export type GreekCarpet = CellsEntry & { type: 'greek-carpet' };
/** A path joins the two `cells` without stepping on a killer cage's highest or lowest digit. The
 * setter's end cells stay drawn. */
export type EscapePath = CellsEntry & { type: 'escape-path' };
/** All `groups` (lists of cells) have the same sum, which the solver finds. A rule over the
 * setter's shapes, which stay drawn. */
export type SameSum = Base & { type: 'same-sum'; groups: Rc[][] };
/** An arrow in the first of `cells`: the digit in the second (by the arrow's base) appears again
 * `direction`-wards, as many cells away as the arrow cell's digit. The setter's arrow stays drawn. */
export type CopyArrow = CellsEntry & { type: 'copy-arrow'; direction: [number, number] };
/** Dots of a few kinds (`groups`: per kind, its pairs of cells): each kind one unknown ratio or
 * difference (`kind`), different kinds different ones. The setter's shapes stay drawn. */
/** Four colours, none repeated a king's or knight's move apart; in boxes side by side (`across`:
 * rows, the default, or columns) the same digit doesn't share a colour. A rule in words. */
export type FourColours = Base & { type: 'four-colours'; across?: 'rows' | 'columns' };
/** A thermo (`cells`, from the bulb) split where the shading changes, each stretch a thermo of an
 * allowed length (`lengths`). Drawn as a thermo. */
export type SplitThermo = CellsEntry & { type: 'split-thermo'; lengths?: number[] };
/** The digits in `cells` sum to `sum` (`seen`: more cells that have to show under fog). A rule in
 * words beside the setter's outside number. */
export type RunSum = CellsEntry & { type: 'run-sum'; sum: number; seen?: Rc[] };
/** Japanese sums along `cells` (from the clues' side): its shaded runs sum, in order, to `totals`,
 * or to ten times `tens` plus each of `clues`' digits. A rule in words beside the setter's cages. */
/** Chaos construction: the regions (`size` cells each; the digit count when not given) are part
 * of the answer; `walls` (pairs of cells) part regions. The grid has no regions of its own. */
export type ChaosRegions = Base & { type: 'chaos-regions'; size?: number; walls?: [Rc, Rc][] };
/** Chaos construction clues: a circle counting its region's cells it sees (`region-view`), an
 * arrow counting the borders it crosses (`border-count`, with `direction`), cells each in a region
 * of their own (`different-regions`). The setter's drawings stay. */
export type RegionView = CellsEntry & { type: 'region-view' };
export type BorderCount = CellsEntry & { type: 'border-count'; direction: [number, number] };
export type DifferentRegions = CellsEntry & { type: 'different-regions' };
/** Loops: one loop through cells (`loop`, an answer layer the player draws with the pen), Masyu
 * circles (`black` or white), digits along the loop apart by `difference` (`loop-whispers`), a
 * circle counting the loop's straight stretches off it (`loop-count`). The setter's circles stay. */
export type Loop = Base & { type: 'loop' };
export type MasyuCircle = CellsEntry & { type: 'masyu-circle'; black?: boolean };
export type LoopWhispers = Base & { type: 'loop-whispers'; difference?: number };
export type LoopCount = CellsEntry & { type: 'loop-count' };
/** Loopy Tiles: a circle counting the cells with its tile (`tile-count`), a dot the loop doesn't
 * pass, its digits not consecutive with `apart` (`loop-block`). The setter's drawings stay. */
export type TileCount = CellsEntry & { type: 'tile-count' };
export type LoopBlock = CellsEntry & { type: 'loop-block'; apart?: boolean };
/** Cookie Crime Part 3: one path from its start (`cells`), leaving it by `direction`, to one other
 * end (`path-end`), never touching itself with `apart`; digits rise along it within each box
 * (`path-thermos`). An answer layer like the loop's; the start's and end's labels stay. */
export type Path = CellsEntry & {
  type: 'path';
  direction?: [number, number];
  apart?: boolean;
};
export type PathEnd = CellsEntry & { type: 'path-end' };
export type PathThermos = Base & { type: 'path-thermos' };
export type JapaneseSums = CellsEntry & {
  type: 'japanese-sums';
  totals?: number[];
  clues?: Rc[];
  tens?: number[];
};
export type UnknownDots = Base & {
  type: 'unknown-dots';
  kind: 'ratio' | 'difference';
  groups: [Rc, Rc][][];
};
/** No digit repeats among `cells` (`of` names them: "the cages"). A rule in words, not drawn. */
export type DistinctDigits = CellsEntry & { type: 'distinct-digits'; of?: string };
export type HitpointArrow = CellsEntry & { type: 'hitpoint-arrow'; direction: [number, number] };
/** Exactly one digit repeats in the cage (`cells`), as a pair. */
export type Cellmates = CellsEntry & { type: 'cellmates' };
/** Outside the killer cages a digit sums to 10 with an orthogonal neighbour; inside, with none. */
export type XCellmates = Base & { type: 'x-cellmates' };
/** The perimeter (`cells`, clockwise): moving each digit's number of steps clockwise, every
 * cell comes back to itself. */
export type Roundabout = CellsEntry & { type: 'roundabout' };
/** Pairs of cells (`cells`: the first two, the next two, …) that each sum to `sum`: corresponding
 * cells of two regions of the same shape. The shaded regions stay drawn. */
export type PairSums = CellsEntry & { type: 'pair-sums'; sum: number };
/** Divided Attention: an arrow outside an end of a row or column (`clues`: the cells just
 * outside) exactly where its first N digits sum to a multiple of N, N being its digit on the
 * marked `diagonal`. The setter's arrows stay drawn. */
/** N-Mates: the arrow's line (`cells`, from the arrow's end) and its `direction`: the arrow cell's
 * digit is how far its mate is; the two sum to the line's largest digit. Arrows stay drawn. */
/** Oil and water: each row of the cage (`cells`) holds one parity, even rows above odd ones. */
export type OilWater = CellsEntry & { type: 'oil-water' };
/** Every 2×2 holds a digit of each of `sets`. */
export type SetSquares = Base & { type: 'set-squares'; sets: (string | number)[][] };
/** Sissa's line (`cells`): from one end each value doubles, digits are digital roots, only the
 * first matches its value. The setter's line stays drawn. */
export type SissaLine = CellsEntry & { type: 'sissa-line' };
export type NMates = CellsEntry & { type: 'n-mates'; direction: [number, number] };
export type DivisorArrows = Base & {
  type: 'divisor-arrows';
  diagonal: 'negative' | 'positive';
  clues: Rc[];
};
/** Two cages' sums (the first `first` cells are one cage): consecutive (`white`) or one twice the
 * other (`black`). The setter's diamond stays drawn. */
export type CageKropki = CellsEntry & {
  type: 'cage-kropki';
  first: number;
  kind: 'white' | 'black';
};

/** Round 7c: values that differ from the digits. Rules in words, not drawn. */
export type NegativeDigits = Base & {
  type: 'negative-digits';
  /** Each of these cages holds exactly one negative digit, and none are outside them. */
  cages?: Rc[][];
  /** The same for every killer cage. */
  killerCages?: boolean;
  /** Every row, column and box holds exactly one negative digit. */
  perHouse?: boolean;
  /** No two negative digits share a row, column or box. */
  apart?: boolean;
  /** No two negative digits have the same value. */
  distinct?: boolean;
};
/** Every killer cage (or each of `cages`) assassinates the cell its two non-zero values name. */
export type ContractKiller = Base & { type: 'contract-killer'; cages?: Rc[][] };
export type Liars = Base & {
  type: 'liars';
  perHouse: true;
  distinctDigits?: boolean;
  distinctValues?: boolean;
};
/** The killer cages the line runs through may lie: exactly `count` of them do, or as many as
 * the digit in `countCell`. Drawn as a line. */
export type LiarLine = CellsEntry & { type: 'liar-line'; count?: number; countCell?: Rc };

export const SHADING_TYPES = [
  'shaded',
  'connected',
  'no-2x2',
  'area-size',
  'reach-edge',
  'shade-count',
  'view-count',
  'shaded-fortress',
  'shaded-ratio',
  'area-clues',
  'area-digits',
  'snake',
  'snake-sequence',
  'snake-turns',
  'shaded-whispers',
  'unshaded-sum',
  'mobius',
] as const;

/** Every constraint type with a plugin (Packs 0–5). */
export type KnownConstraint =
  | ExtraRegion
  | Diagonal
  | DisjointGroups
  | Odd
  | Even
  | Minimum
  | Maximum
  | Fortress
  | Line
  | Arrow
  | Killer
  | LittleKiller
  | OutsideClue
  | Quadruple
  | EdgeClue
  | NegativeEdge
  | AntiKnight
  | AntiKing
  | NonConsecutive
  | Clone
  | MagicSquare
  | Queen
  | KnightDistinct
  | ChessKings
  | CageColours
  | Friendly
  | BoxIndexer
  | Diversity
  | Wheel
  | AntiDiagonal
  | Either
  | Shaded
  | Connected
  | NoSquare
  | AreaSize
  | ReachEdge
  | ShadeCount
  | ViewCount
  | ShadedFortress
  | ShadedRatio
  | AreaClues
  | AreaDigits
  | Snake
  | SnakeSequence
  | SnakeTurns
  | ShadedWhispers
  | UnshadedSum
  | Mobius
  | ProductLine
  | Contains
  | SameParity
  | CountingCircles
  | NeverBoth
  | NoConsecutiveTriple
  | DominantLine
  | Battenburg
  | NegativeDigits
  | ContractKiller
  | Liars
  | LiarLine
  | WhisperSomewhere
  | CageKropki
  | PointerSum
  | HitpointArrow
  | Cellmates
  | XCellmates
  | Roundabout
  | PairSums
  | DivisorArrows
  | NMates
  | OilWater
  | SetSquares
  | SissaLine
  | BoxPointer
  | LargestFirst
  | DistinctDigits
  | GreekCarpet
  | EscapePath
  | SameSum
  | CopyArrow
  | UnknownDots
  | FourColours
  | SplitThermo
  | RunSum
  | JapaneseSums
  | ChaosRegions
  | RegionView
  | BorderCount
  | DifferentRegions
  | Loop
  | MasyuCircle
  | LoopWhispers
  | LoopCount
  | TileCount
  | LoopBlock
  | Path
  | PathEnd
  | PathThermos;

export type KnownType = KnownConstraint['type'];

export const KNOWN_TYPES: readonly KnownType[] = [
  'extra-region',
  'diagonal',
  'disjoint-groups',
  'odd',
  'even',
  'minimum',
  'maximum',
  'fortress',
  ...LINE_TYPES,
  'arrow',
  'killer',
  'little-killer',
  ...OUTSIDE_TYPES,
  'quadruple',
  ...EDGE_TYPES,
  'anti-knight',
  'anti-king',
  'non-consecutive',
  'clone',
  'magic-square',
  'queen',
  'knight-distinct',
  'chess-kings',
  'cage-colours',
  'friendly',
  'box-indexer',
  'wheel',
  'diversity',
  'anti-diagonal',
  'either',
  ...SHADING_TYPES,
  'product-line',
  'contains',
  'same-parity',
  'counting-circles',
  'never-both',
  'no-consecutive-triple',
  'dominant-line',
  'battenburg',
  'negative-digits',
  'contract-killer',
  'liars',
  'liar-line',
  'whisper-somewhere',
  'cage-kropki',
  'pointer-sum',
  'hitpoint-arrow',
  'cellmates',
  'x-cellmates',
  'roundabout',
  'pair-sums',
  'divisor-arrows',
  'n-mates',
  'oil-water',
  'set-squares',
  'sissa-line',
  'box-pointer',
  'largest-first',
  'distinct-digits',
  'greek-carpet',
  'escape-path',
  'same-sum',
  'copy-arrow',
  'unknown-dots',
  'four-colours',
  'split-thermo',
  'run-sum',
  'japanese-sums',
  'chaos-regions',
  'region-view',
  'border-count',
  'different-regions',
  'loop',
  'masyu-circle',
  'loop-whispers',
  'loop-count',
  'tile-count',
  'loop-block',
  'path',
  'path-end',
  'path-thermos',
];

/** A document entry: a known constraint or one the core doesn't know yet. */
export type ConstraintEntry = { type: string } & Record<string, unknown>;

export function isKnown(entry: ConstraintEntry): entry is ConstraintEntry & KnownConstraint {
  return (KNOWN_TYPES as readonly string[]).includes(entry.type);
}

/** Names for tags, rules text and the library. */
export const CONSTRAINT_NAMES: Record<KnownType, string> = {
  'extra-region': 'Extra regions',
  diagonal: 'Diagonals',
  'disjoint-groups': 'Disjoint groups',
  odd: 'Odd cells',
  even: 'Even cells',
  minimum: 'Minimum cells',
  maximum: 'Maximum cells',
  fortress: 'Fortress',
  thermo: 'Thermo',
  'slow-thermo': 'Slow thermo',
  arrow: 'Arrow',
  'double-arrow': 'Double arrow',
  renban: 'Renban',
  nabner: 'Nabner',
  whispers: 'Whispers',
  palindrome: 'Palindrome',
  'region-sum': 'Region sum',
  between: 'Between lines',
  lockout: 'Lockout',
  entropic: 'Entropic',
  modular: 'Modular',
  parity: 'Parity lines',
  zipper: 'Zipper',
  killer: 'Killer',
  'little-killer': 'Little killer',
  sandwich: 'Sandwich',
  'x-sum': 'X-sums',
  skyscraper: 'Skyscrapers',
  'frame-sum': 'Frame sums',
  battlefield: 'Battlefield',
  quadruple: 'Quadruples',
  difference: 'Kropki',
  ratio: 'Kropki',
  'edge-sum': 'XV',
  'greater-than': 'Greater than',
  'anti-knight': 'Anti-knight',
  'anti-king': 'Anti-king',
  'non-consecutive': 'Non-consecutive',
  clone: 'Clones',
  'magic-square': 'Magic square',
  queen: 'Queens',
  'knight-distinct': 'Knights on digits',
  'chess-kings': 'Chess kings',
  'cage-colours': 'Coloured cages',
  'kropki-line': 'Kropki lines',
  'kropki-either': 'Grey dots',
  sequence: 'Sequence lines',
  'bulbless-thermo': 'Bulbless thermo',
  'pointing-thermo': 'Pointing thermo',
  'numbered-room': 'Numbered rooms',
  'outside-digits': 'Outside digits',
  'increasing-run': 'Increasing runs',
  'up-to-sum': 'Up-to-N sums',
  friendly: 'Friendly cells',
  'box-indexer': 'Box indexing',
  wheel: 'Wheels',
  diversity: 'Diversity circles',
  'anti-diagonal': 'Anti-diagonal',
  either: 'Either rule',
  shaded: 'Shading',
  connected: 'Shading',
  'no-2x2': 'Shading',
  'area-size': 'Shading',
  'reach-edge': 'Shading',
  'shade-count': 'Shading',
  'view-count': 'View counts',
  'shaded-fortress': 'Shading',
  'shaded-ratio': 'Shading',
  'area-clues': 'Shading',
  'area-digits': 'Shading',
  snake: 'Snake',
  'snake-sequence': 'Snake',
  'snake-turns': 'Snake',
  'shaded-whispers': 'Snake',
  'unshaded-sum': 'Killer',
  mobius: 'Möbius strip',
  'product-line': 'Product lines',
  contains: 'Listed digits',
  'same-parity': 'Same parity',
  'counting-circles': 'Counting circles',
  'never-both': 'Never both even',
  'no-consecutive-triple': 'No three consecutive',
  'dominant-line': 'Dominant lines',
  battenburg: 'Battenburg',
  'negative-digits': 'Negative digits',
  'contract-killer': 'Contract killers',
  liars: 'Liars',
  'liar-line': 'Liar lines',
  'whisper-somewhere': 'Hidden whispers',
  'cage-kropki': 'Kropki cage sums',
  'pointer-sum': 'Pointers',
  'hitpoint-arrow': 'Hitpoint arrows',
  cellmates: 'Cellmates',
  'x-cellmates': 'X-Cellmates',
  roundabout: 'Roundabout',
  'pair-sums': 'Twin sums',
  'divisor-arrows': 'Divisor arrows',
  'n-mates': 'N-Mates',
  'oil-water': 'Oil and water',
  'set-squares': 'Sets in every 2×2',
  'sissa-line': "Sissa's lines",
  'box-pointer': 'Arrows through the boxes',
  'largest-first': 'Largest in the top right',
  'distinct-digits': 'No repeats across a clue type',
  'greek-carpet': 'Greek carpets',
  'escape-path': 'Escape path',
  'same-sum': 'Groups of the same sum',
  'copy-arrow': 'Copying arrows',
  'unknown-dots': 'Dots of unknown ratio',
  'four-colours': 'Four colours',
  'split-thermo': 'Thermos split by the shading',
  'run-sum': "Sums of a snake's stretches",
  'japanese-sums': 'Japanese sums',
  'chaos-regions': 'Chaos construction',
  'region-view': 'Region views',
  'border-count': 'Border counts',
  'different-regions': 'Different regions',
  loop: 'Loop',
  'masyu-circle': 'Masyu circles',
  'loop-whispers': 'Whispers along the loop',
  'loop-count': 'Loop stretch counts',
  'tile-count': 'Tile counts',
  'loop-block': 'Loop blocks',
  path: 'Path',
  'path-end': 'Path end',
  'path-thermos': 'Thermos along the path',
};
